import { nanoid } from '@reduxjs/toolkit'
import { useTranslation } from 'next-i18next/pages'
import { GenerateButton, Panel } from 'pcl-ui'
import React from 'react'
import { track } from 'track'
import { useAppDispatch, useAppSelector } from '../state/hooks'
import {
  addOption,
  addReasons,
  clearGenerationError,
  setAiDisabled,
  setGenerationError,
  setIsGenerating,
  setLastAddedOptionId,
} from '../state/reducers'
import { isAiDisabled as checkAiDisabled } from '../utils/aiFeatureStatus'
import { generateReasons } from '../utils/generateReasonsApi'
import {
  hasOversizedExistingReasonGenerationRequest,
  isGenerateReasonsRequestWithinSizeLimit,
} from '../utils/generateReasonsRequestSize'

export function AddOptionForm({
  hasExistingOptions,
  seedValue,
}: {
  hasExistingOptions?: boolean
  seedValue?: string
} = {}) {
  const [optionValue, setOptionValue] = React.useState('')

  React.useEffect(() => {
    if (seedValue) setOptionValue(seedValue)
  }, [seedValue])
  const [isLocalGenerating, setIsLocalGenerating] = React.useState(false)
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const reasons = useAppSelector((state) => state.reasons)
  const existingOptions = useAppSelector((state) => state.options)
  const reduxOptionCount = existingOptions.length
  const optionCount = hasExistingOptions
    ? Math.max(reduxOptionCount, 1)
    : reduxOptionCount
  const isGenerating = useAppSelector(
    (state) => state.meta.isGenerating ?? false,
  )
  const aiDisabled = useAppSelector((state) => state.meta.aiDisabled ?? false)

  // Check AI disabled status after hydration to avoid SSR mismatch
  React.useEffect(() => {
    dispatch(setAiDisabled(checkAiDisabled()))
  }, [dispatch])

  function parseOptions(input: string): string[] {
    return input
      .split(/[,、，]/)
      .map((option) => option.trim())
      .filter((option) => option.length > 0)
  }

  function handleAdd() {
    if (optionValue === '') return

    const options = parseOptions(optionValue)
    if (options.length > 1) {
      track('event', 'multi-add-options', { option_count: options.length })
    }

    const optionsWithIds = options.map((optionLabel) => ({
      label: optionLabel,
      id: nanoid(4),
    }))

    if (optionsWithIds.length > 0) {
      dispatch(setLastAddedOptionId(optionsWithIds[0].id))
    }

    optionsWithIds.forEach((option) => {
      dispatch(addOption(option))
    })

    setOptionValue('')
  }

  async function handleGenerate() {
    if (optionValue === '') return

    const options = parseOptions(optionValue).slice(0, 3)
    if (options.length === 0) return

    if (options.length > 1) {
      track('event', 'ai-multi-generate-options', {
        option_count: options.length,
      })
    }

    dispatch(setIsGenerating(true))
    setIsLocalGenerating(true)
    dispatch(clearGenerationError())

    const promises = options.map(async (optionLabel) => {
      const optionId = nanoid(4)
      // Mark the start of AI generation for User Timing API
      const markName = `ai-generate-new-${optionId}`
      if (typeof performance !== 'undefined') {
        performance.mark(`${markName}-start`)
      }

      try {
        // Only send existing reasons if showAiGenerateForExistingOptions is enabled
        const response = await generateReasons({
          optionLabel,
          optionId,
          reasons:
            process.env.FEATURES_SHOW_AI_GENERATE_FOR_EXISTING_OPTIONS ===
            'true'
              ? reasons
              : undefined,
          language: i18n.language,
        })

        // Mark the end and create measure for User Timing API
        if (typeof performance !== 'undefined') {
          performance.mark(`${markName}-end`)
          performance.measure(
            `AI Generation (${optionLabel})`,
            `${markName}-start`,
            `${markName}-end`,
          )
        }

        // Log safety identifier in dev mode
        if (response.safetyIdentifier && response.clientIp) {
          console.log('Safety Identifier:', response.safetyIdentifier)
          console.log('Client IP:', response.clientIp)
        }

        dispatch(addOption({ label: optionLabel, id: optionId }))

        const hydratedReasons = response.reasons.map((reason) => ({
          ...reason,
          optionId,
          weight: 0,
        }))
        dispatch(addReasons(hydratedReasons))

        track('event', 'ai-generate-reasons', {
          reasons_count: response.reasons.length,
          context: 'new_option',
        })

        return { success: true, optionId, optionLabel }
      } catch (err) {
        // Even if reason generation fails, still add the option
        dispatch(addOption({ label: optionLabel, id: optionId }))

        const errorMessage =
          err instanceof Error
            ? err.message
            : t('AddOptionForm.errorGenerating')

        dispatch(
          setGenerationError({
            optionId,
            message: errorMessage || t('AddOptionForm.errorGenerating'),
          }),
        )

        track('event', 'ai-generate-reasons-error', {
          error_message: errorMessage,
          context: 'new_option',
        })

        return { success: false, optionId, optionLabel, error: errorMessage }
      }
    })

    try {
      const results = await Promise.all(promises)

      if (results.length > 0) {
        dispatch(setLastAddedOptionId(results[0].optionId))
      }

      const hasErrors = results.some((result) => !result.success)
      if (hasErrors) {
        dispatch(setAiDisabled(true))
      }

      setOptionValue('')
    } finally {
      dispatch(setIsGenerating(false))
      setIsLocalGenerating(false)
    }
  }

  const showAi = process.env.FEATURES_SHOW_AI_FEATURES === 'true' && !aiDisabled
  const optionsToGenerate = parseOptions(optionValue).slice(0, 3)
  const generationRequestsFitSizeLimit = optionsToGenerate.every(
    (optionLabel) =>
      isGenerateReasonsRequestWithinSizeLimit({
        optionLabel,
        // New option IDs are always four ASCII characters (nanoid(4)).
        optionId: '0000',
        reasons:
          process.env.FEATURES_SHOW_AI_GENERATE_FOR_EXISTING_OPTIONS === 'true'
            ? reasons
            : undefined,
        language: i18n.language,
      }),
  )
  const hasOversizedExistingRequest =
    hasOversizedExistingReasonGenerationRequest(
      existingOptions,
      reasons,
      i18n.language,
    )

  return (
    <Panel>
      <input
        data-test="option-input"
        className="w-full text-base md:text-sm font-semibold px-4 py-3.5 bg-transparent outline-none text-ink dark:text-dark-ink placeholder:text-[rgba(128,128,128,0.55)] dark:placeholder:text-[#908C87] transition-colors border-b border-ink/15 dark:border-dark-border/15"
        type="text"
        name="Add Options"
        onChange={(e) => setOptionValue(e.currentTarget.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        placeholder={t(
          optionCount > 0
            ? 'AddOptionForm.placeholderAnother'
            : 'AddOptionForm.placeholder',
        )}
        value={optionValue}
        disabled={isGenerating}
      />
      <div className="flex">
        <button
          type="button"
          onClick={handleAdd}
          disabled={isGenerating || optionValue === ''}
          data-test="add-option-button"
          className="flex-[2] py-3.5 text-xs font-extrabold tracking-widest uppercase bg-ink text-surface dark:bg-transparent dark:text-dark-ink border-t-2 border-r-2 border-ink dark:border-dark-border hover:opacity-80 dark:hover:opacity-100 dark:hover:bg-white/[0.06] disabled:cursor-not-allowed transition-[opacity,background-color] cursor-pointer"
        >
          {t('AddOptionForm.Add')}
        </button>
        {showAi && (
          <GenerateButton
            onClick={handleGenerate}
            disabled={
              isGenerating ||
              optionValue === '' ||
              !generationRequestsFitSizeLimit ||
              hasOversizedExistingRequest
            }
            isGenerating={isLocalGenerating}
            generateLabel={t('AddOptionForm.Generate')}
            generatingLabel={t('AddOptionForm.Generating')}
            data-test="generate-option-button"
            className="flex-1 py-3.5 text-xs font-extrabold tracking-widest uppercase text-ink dark:text-dark-ink bg-transparent border-t-2 border-ink dark:border-dark-border hover:bg-surface-muted dark:hover:bg-dark-bg2 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          />
        )}
      </div>
    </Panel>
  )
}
